'use client';

import { Calendar, Filter } from 'lucide-react';

export type Timeframe = '7d' | '30d' | '90d' | 'ytd' | 'all';
export type SportFilter = 'all' | 'NFL' | 'NBA' | 'NHL' | 'MLB' | 'NCAAB' | 'Soccer';
export type BetTypeFilter = 'all' | 'spread' | 'moneyline' | 'total' | 'prop' | 'parlay';

interface ReportingFiltersProps {
  timeframe: Timeframe;
  setTimeframe: (t: Timeframe) => void;
  sport: SportFilter;
  setSport: (s: SportFilter) => void;
  betType: BetTypeFilter;
  setBetType: (b: BetTypeFilter) => void;
}

const timeframes: { value: Timeframe; label: string }[] = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'all', label: 'All Time' },
];

const sports: { value: SportFilter; label: string }[] = [
  { value: 'all', label: 'All Sports' },
  { value: 'NFL', label: '🏈 NFL' },
  { value: 'NBA', label: '🏀 NBA' },
  { value: 'NHL', label: '🏒 NHL' },
  { value: 'MLB', label: '⚾ MLB' },
  { value: 'NCAAB', label: '🏀 NCAAB' },
  { value: 'Soccer', label: '⚽ Soccer' },
];

const betTypes: { value: BetTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'spread', label: 'Spread' },
  { value: 'moneyline', label: 'Moneyline' },
  { value: 'total', label: 'Total (O/U)' },
  { value: 'prop', label: 'Prop' },
  { value: 'parlay', label: 'Parlay' },
];

export default function ReportingFilters({
  timeframe, setTimeframe, sport, setSport, betType, setBetType,
}: ReportingFiltersProps) {
  return (
    <div className="flex flex-col lg:flex-row gap-4 mb-8">
      {/* Timeframe tabs */}
      <div className="flex items-center gap-1 p-1 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl flex-wrap">
        {timeframes.map((t) => (
          <button
            key={t.value}
            onClick={() => setTimeframe(t.value)}
            className={`tab-btn ${timeframe === t.value ? 'active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {/* Sport filter */}
        <div className="relative">
          <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))]" />
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value as SportFilter)}
            className="select-field pl-8 pr-8 text-sm min-w-[140px]"
            style={{ backgroundImage: 'none' }}
          >
            {sports.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Bet type filter */}
        <div className="relative">
          <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))]" />
          <select
            value={betType}
            onChange={(e) => setBetType(e.target.value as BetTypeFilter)}
            className="select-field pl-8 pr-8 text-sm min-w-[140px]"
            style={{ backgroundImage: 'none' }}
          >
            {betTypes.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}